import { useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Container } from '@mui/material';
import { COLORS } from '../../theme/themeConstants';
import { useSettlementsApi } from '../../Hooks/useSettlementsAPI';

/**
 * This component handles the automatic return from HyperPay for settlement payments
 * It verifies the payment and redirects to the success page
 */
const SettlementHyperPayReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifySettlementPayment } = useSettlementsApi();

  const settlementPaymentId = useMemo(() => searchParams.get('settlementPaymentId') || '', [searchParams]);
  const checkoutId = useMemo(() => 
    searchParams.get('id') || 
    searchParams.get('checkoutId') || 
    searchParams.get('resourcePath')?.split('/').pop() || 
    '', 
    [searchParams]
  );
  const merchantIdParam = useMemo(() => searchParams.get('merchantId') || '', [searchParams]);
  const resourcePath = useMemo(() => searchParams.get('resourcePath') || '', [searchParams]);

  useEffect(() => {
    const verifyAndRedirect = async () => {
      // Get merchant ID from params or localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const merchantId: string = merchantIdParam || userData?.userId;
      
      if (!merchantId || !checkoutId) {
        console.error('Missing required parameters:', { merchantId, checkoutId });
        navigate('/layout/settlements');
        return;
      }

      try {
        console.log('Verifying settlement payment with:', { checkoutId, merchantId, settlementPaymentId, resourcePath });
        
        // Verify the payment with backend
        const response = await verifySettlementPayment({ 
          checkoutId: String(checkoutId), 
          merchantId: String(merchantId),
          settlementPaymentId: String(settlementPaymentId)
        });

        // Redirect to success page with the result
        if (Boolean(response.data.success)) {
          navigate(`/payment/settlement/hyperpay/success?status=success&settlementPaymentId=${settlementPaymentId}&id=${checkoutId}&merchantId=${merchantId}`);
        } else {
          navigate(`/payment/settlement/hyperpay/success?status=failed&settlementPaymentId=${settlementPaymentId}&id=${checkoutId}&merchantId=${merchantId}&message=${encodeURIComponent(response.data.message || 'Payment verification failed')}`);
        }
      } catch (err) {
        console.error('Settlement payment verification error:', err);
        navigate(`/payment/settlement/hyperpay/success?status=error&settlementPaymentId=${settlementPaymentId}&id=${checkoutId}&merchantId=${merchantId}&message=${encodeURIComponent('Payment verification failed')}`);
      }
    };

    // Start verification immediately
    verifyAndRedirect();
  }, [checkoutId, merchantIdParam, settlementPaymentId, resourcePath, verifySettlementPayment, navigate]);

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)'
    }}>
      <Container maxWidth="sm">
        <Paper sx={{
          p: 4,
          border: '1px solid #e8eaed',
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          textAlign: 'center'
        }}>
          <Box sx={{ mb: 4 }}>
            <CircularProgress 
              size={60} 
              sx={{ 
                color: COLORS.primary.main,
                mb: 2
              }} 
            />
            
            <Typography variant="h5" sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}>
              Processing Your Settlement Payment
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
              Please wait while we verify your payment...
            </Typography>
            
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Do not close this window
            </Typography>
          </Box>

          {settlementPaymentId && (
            <Typography variant="caption" sx={{
              color: 'text.secondary',
              opacity: 0.7
            }}>
              Settlement Payment: {settlementPaymentId}
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default SettlementHyperPayReturn;
